import {
  Component, ElementRef, OnDestroy, OnInit, ViewChild, inject, signal, computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AuthService } from '../../core/services/auth.service';
import { ChatRealtimeService } from './chat-realtime.service';
import { ChatService } from './chat.service';
import {
  AttachmentInput,
  ChatAttachment,
  ChatConversation,
  ChatMember,
  ChatMessage,
  ChatSearchHit,
  ChatUserOption,
  PRESENCE_ONLINE,
  ROOM_DIRECT
} from './chat.models';

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [
    CommonModule, FormsModule, RouterLink,
    MatIconModule, MatButtonModule, MatFormFieldModule, MatInputModule,
    MatMenuModule, MatProgressSpinnerModule, MatTooltipModule
  ],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss'
})
export class ChatComponent implements OnInit, OnDestroy {
  @ViewChild('threadScroll') threadScroll?: ElementRef<HTMLDivElement>;
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  private readonly chat = inject(ChatService);
  private readonly realtime = inject(ChatRealtimeService);
  private readonly auth = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();
  private readonly search$ = new Subject<string>();
  private typingTimer: ReturnType<typeof setTimeout> | null = null;
  private currentRoomId: string | null = null;
  private loadingOlder = false;
  private hasMore = true;

  readonly loading = signal(false);
  readonly loadingMessages = signal(false);
  readonly error = signal<string | null>(null);
  readonly conversations = signal<ChatConversation[]>([]);
  readonly selected = signal<ChatConversation | null>(null);
  readonly messages = signal<ChatMessage[]>([]);
  readonly members = signal<ChatMember[]>([]);
  readonly sharedFiles = signal<ChatAttachment[]>([]);
  readonly sharedImages = signal<ChatAttachment[]>([]);
  readonly pinned = signal<ChatMessage[]>([]);
  readonly searchHits = signal<ChatSearchHit[]>([]);
  readonly directory = signal<ChatUserOption[]>([]);
  readonly typingLabel = signal<string | null>(null);
  readonly rightOpen = signal(true);
  readonly composer = signal('');
  readonly replyTo = signal<ChatMessage | null>(null);
  readonly pendingAttachments = signal<AttachmentInput[]>([]);
  readonly uploadProgress = signal(false);
  readonly sidebarTab = signal<'chats' | 'groups'>('chats');
  readonly showNewDm = signal(false);
  readonly showNewGroup = signal(false);
  readonly groupName = signal('');
  readonly groupDesc = signal('');
  readonly groupMembers = signal<string[]>([]);
  readonly dirSearch = signal('');
  readonly sidebarSearch = signal('');
  readonly editingId = signal<string | null>(null);
  readonly editDraft = signal('');
  readonly previewImage = signal<string | null>(null);

  readonly me = computed(() => this.auth.user()?.id ?? '');
  readonly filteredConversations = computed(() => {
    const tab = this.sidebarTab();
    const q = this.sidebarSearch().trim().toLowerCase();
    return this.conversations()
      .filter((c) => !c.isArchived)
      .filter((c) => tab === 'groups' ? c.roomType !== ROOM_DIRECT : true)
      .filter((c) => !q || c.name.toLowerCase().includes(q) || (c.lastMessagePreview ?? '').toLowerCase().includes(q))
      .sort((a, b) => Number(b.isPinned) - Number(a.isPinned) || (b.lastMessageAt ?? '').localeCompare(a.lastMessageAt ?? ''));
  });

  readonly pinnedConversations = computed(() => this.filteredConversations().filter((c) => c.isPinned));
  readonly recentConversations = computed(() => this.filteredConversations().filter((c) => !c.isPinned));

  ngOnInit(): void {
    this.loadConversations();
    void this.realtime.ensureConnected().catch(() => undefined);
    this.chat.updatePresence(PRESENCE_ONLINE).subscribe({ error: () => undefined });

    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const roomId = params.get('roomId');
      if (roomId && roomId !== this.currentRoomId) {
        const existing = this.conversations().find((c) => c.id === roomId);
        if (existing) void this.openConversation(existing);
        else this.chat.getConversation(roomId).subscribe({
          next: (c) => void this.openConversation(c),
          error: () => undefined
        });
      }
    });

    this.search$.pipe(debounceTime(280), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((q) => {
      if (!q.trim()) {
        this.searchHits.set([]);
        return;
      }
      this.chat.search(q.trim()).subscribe({
        next: (hits) => this.searchHits.set(hits),
        error: () => this.searchHits.set([])
      });
    });

    this.realtime.messageCreated$.pipe(takeUntil(this.destroy$)).subscribe((m) => {
      if (m.roomId !== this.currentRoomId) {
        this.conversations.update((list) => list.map((c) =>
          c.id === m.roomId
            ? { ...c, lastMessageAt: m.createdAt, lastMessagePreview: m.content || c.lastMessagePreview, unreadCount: c.unreadCount + 1 }
            : c));
        return;
      }
      this.messages.update((list) => list.some((x) => x.id === m.id) ? list : [...list, m]);
      this.scrollToBottom();
      void this.realtime.markRead(m.roomId, m.id);
    });

    this.realtime.messageUpdated$.pipe(takeUntil(this.destroy$)).subscribe((m) => {
      if (m.roomId !== this.currentRoomId) return;
      this.messages.update((list) => list.map((x) => x.id === m.id ? m : x));
    });

    this.realtime.messageDeleted$.pipe(takeUntil(this.destroy$)).subscribe((e) => {
      if (e.roomId !== this.currentRoomId) return;
      if (e.forEveryone) {
        this.messages.update((list) => list.map((m) =>
          m.id === e.messageId ? { ...m, deletedForEveryone: true, content: '', attachments: [] } : m));
      } else {
        this.messages.update((list) => list.filter((m) => m.id !== e.messageId));
      }
    });

    this.realtime.typing$.pipe(takeUntil(this.destroy$)).subscribe((e) => {
      if (e.roomId !== this.currentRoomId || e.userId === this.me()) return;
      this.typingLabel.set(e.isTyping ? `${e.displayName} is typing…` : null);
    });

    this.realtime.presenceChanged$.pipe(takeUntil(this.destroy$)).subscribe((p) => {
      this.conversations.update((list) => list.map((c) =>
        c.peerUserId === p.userId ? { ...c, peerPresence: p.status, peerLastSeenAt: p.lastSeenAt } : c));
    });
  }

  ngOnDestroy(): void {
    if (this.currentRoomId) void this.realtime.leaveRoom(this.currentRoomId);
    this.chat.updatePresence(0).subscribe({ error: () => undefined });
    this.destroy$.next();
    this.destroy$.complete();
    if (this.typingTimer) clearTimeout(this.typingTimer);
  }

  loadConversations(): void {
    this.loading.set(true);
    this.error.set(null);
    this.chat.listConversations().subscribe({
      next: (items) => {
        this.conversations.set(items ?? []);
        this.loading.set(false);
        const roomId = this.route.snapshot.queryParamMap.get('roomId');
        if (roomId) {
          const found = items.find((c) => c.id === roomId);
          if (found) void this.openConversation(found);
        }
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load conversations.');
        this.loading.set(false);
      }
    });
  }

  onSidebarSearch(value: string): void {
    this.sidebarSearch.set(value);
    this.search$.next(value);
  }

  async openConversation(c: ChatConversation): Promise<void> {
    if (this.currentRoomId && this.currentRoomId !== c.id) {
      await this.realtime.leaveRoom(this.currentRoomId);
    }
    this.currentRoomId = c.id;
    this.selected.set(c);
    this.replyTo.set(null);
    this.pendingAttachments.set([]);
    this.hasMore = true;
    this.messages.set([]);
    this.loadingMessages.set(true);
    await this.realtime.joinRoom(c.id).catch(() => undefined);
    void this.router.navigate([], { queryParams: { roomId: c.id }, queryParamsHandling: 'merge', replaceUrl: true });

    this.chat.listMessages(c.id, 40).subscribe({
      next: (msgs) => {
        this.messages.set(msgs ?? []);
        this.loadingMessages.set(false);
        this.hasMore = (msgs?.length ?? 0) >= 40;
        setTimeout(() => this.scrollToBottom(), 40);
        const last = msgs?.[msgs.length - 1];
        this.chat.markRead(c.id, last?.id).subscribe({ error: () => undefined });
        this.conversations.update((list) => list.map((x) => x.id === c.id ? { ...x, unreadCount: 0 } : x));
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load messages.');
        this.loadingMessages.set(false);
      }
    });

    this.refreshRightPanel(c.id);
  }

  refreshRightPanel(roomId: string): void {
    this.chat.listMembers(roomId).subscribe({ next: (m) => this.members.set(m), error: () => this.members.set([]) });
    this.chat.sharedFiles(roomId, false).subscribe({ next: (f) => this.sharedFiles.set(f.filter((x) => !x.isImage)), error: () => this.sharedFiles.set([]) });
    this.chat.sharedFiles(roomId, true).subscribe({ next: (f) => this.sharedImages.set(f), error: () => this.sharedImages.set([]) });
    this.chat.listPinned(roomId).subscribe({ next: (p) => this.pinned.set(p), error: () => this.pinned.set([]) });
  }

  onThreadScroll(): void {
    const el = this.threadScroll?.nativeElement;
    if (!el || this.loadingOlder || !this.hasMore || !this.currentRoomId) return;
    if (el.scrollTop > 80) return;
    const first = this.messages()[0];
    if (!first) return;
    this.loadingOlder = true;
    const prevHeight = el.scrollHeight;
    this.chat.listMessages(this.currentRoomId, 40, first.createdAt).subscribe({
      next: (older) => {
        if (!older?.length) this.hasMore = false;
        else {
          this.messages.update((list) => [...older, ...list]);
          setTimeout(() => {
            el.scrollTop = el.scrollHeight - prevHeight;
          });
        }
        this.loadingOlder = false;
      },
      error: () => { this.loadingOlder = false; }
    });
  }

  onComposerInput(value: string): void {
    this.composer.set(value);
    if (!this.currentRoomId) return;
    void this.realtime.notifyTyping(this.currentRoomId, true);
    if (this.typingTimer) clearTimeout(this.typingTimer);
    this.typingTimer = setTimeout(() => {
      if (this.currentRoomId) void this.realtime.notifyTyping(this.currentRoomId, false);
    }, 1200);
  }

  send(): void {
    const room = this.selected();
    if (!room) return;
    const content = this.composer().trim();
    const attachments = this.pendingAttachments();
    if (!content && !attachments.length) return;

    const reply = this.replyTo();
    this.chat.sendMessage(room.id, {
      content: content || undefined,
      replyToMessageId: reply?.id,
      attachments: attachments.length ? attachments : undefined,
      messageType: attachments.length ? (attachments.every((a) => (a.contentType || '').startsWith('image/')) ? 'Image' : 'File') : 'Text'
    }).subscribe({
      next: (msg) => {
        this.messages.update((list) => list.some((m) => m.id === msg.id) ? list : [...list, msg]);
        this.composer.set('');
        this.replyTo.set(null);
        this.pendingAttachments.set([]);
        this.conversations.update((list) => list.map((c) =>
          c.id === room.id
            ? { ...c, lastMessageAt: msg.createdAt, lastMessagePreview: content || attachments[0]?.originalFileName || c.lastMessagePreview }
            : c));
        this.scrollToBottom();
        this.refreshRightPanel(room.id);
      },
      error: (err) => this.error.set(err?.message || 'Failed to send message.')
    });
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (!files.length) return;
    this.uploadFiles(files);
    input.value = '';
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (files.length) this.uploadFiles(files);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  uploadFiles(files: File[]): void {
    this.uploadProgress.set(true);
    this.chat.upload(files).subscribe({
      next: (atts) => {
        this.pendingAttachments.update((list) => [...list, ...atts]);
        this.uploadProgress.set(false);
      },
      error: (err) => {
        this.error.set(err?.message || 'Upload failed.');
        this.uploadProgress.set(false);
      }
    });
  }

  removePending(index: number): void {
    this.pendingAttachments.update((list) => list.filter((_, i) => i !== index));
  }

  setReply(m: ChatMessage): void {
    this.replyTo.set(m);
  }

  clearReply(): void {
    this.replyTo.set(null);
  }

  startEdit(m: ChatMessage): void {
    this.editingId.set(m.id);
    this.editDraft.set(m.content);
  }

  saveEdit(m: ChatMessage): void {
    const room = this.selected();
    if (!room) return;
    this.chat.editMessage(room.id, m.id, this.editDraft().trim()).subscribe({
      next: (updated) => {
        this.messages.update((list) => list.map((x) => x.id === updated.id ? updated : x));
        this.editingId.set(null);
      },
      error: (err) => this.error.set(err?.message || 'Edit failed.')
    });
  }

  deleteForMe(m: ChatMessage): void {
    const room = this.selected();
    if (!room) return;
    this.chat.deleteForMe(room.id, m.id).subscribe({
      next: () => this.messages.update((list) => list.filter((x) => x.id !== m.id)),
      error: (err) => this.error.set(err?.message || 'Delete failed.')
    });
  }

  deleteForEveryone(m: ChatMessage): void {
    const room = this.selected();
    if (!room) return;
    this.chat.deleteForEveryone(room.id, m.id).subscribe({
      next: () => this.messages.update((list) => list.map((x) =>
        x.id === m.id ? { ...x, deletedForEveryone: true, content: '', attachments: [] } : x)),
      error: (err) => this.error.set(err?.message || 'Delete failed.')
    });
  }

  pinMessage(m: ChatMessage, pinned: boolean): void {
    const room = this.selected();
    if (!room) return;
    this.chat.pinMessage(room.id, m.id, pinned).subscribe({
      next: () => {
        this.messages.update((list) => list.map((x) => x.id === m.id ? { ...x, isPinned: pinned } : x));
        this.refreshRightPanel(room.id);
      },
      error: () => undefined
    });
  }

  react(m: ChatMessage, emoji: string): void {
    const room = this.selected();
    if (!room) return;
    this.chat.toggleReaction(room.id, m.id, emoji).subscribe({
      next: (reactions) => this.messages.update((list) => list.map((x) => x.id === m.id ? { ...x, reactions } : x)),
      error: () => undefined
    });
  }

  copyMessage(m: ChatMessage): void {
    void navigator.clipboard?.writeText(m.content || '');
  }

  forwardTo(m: ChatMessage, target: ChatConversation): void {
    this.chat.forwardMessage(target.id, m.id).subscribe({
      next: () => undefined,
      error: (err) => this.error.set(err?.message || 'Forward failed.')
    });
  }

  togglePinConversation(): void {
    const c = this.selected();
    if (!c) return;
    this.chat.pinConversation(c.id, !c.isPinned).subscribe({
      next: () => {
        this.selected.update((s) => s ? { ...s, isPinned: !s.isPinned } : s);
        this.conversations.update((list) => list.map((x) => x.id === c.id ? { ...x, isPinned: !c.isPinned } : x));
      },
      error: () => undefined
    });
  }

  toggleMute(): void {
    const c = this.selected();
    if (!c) return;
    this.chat.muteConversation(c.id, !c.isMuted).subscribe({
      next: () => {
        this.selected.update((s) => s ? { ...s, isMuted: !s.isMuted } : s);
        this.conversations.update((list) => list.map((x) => x.id === c.id ? { ...x, isMuted: !c.isMuted } : x));
      },
      error: () => undefined
    });
  }

  leaveOrArchive(): void {
    const c = this.selected();
    if (!c) return;
    if (c.roomType === ROOM_DIRECT) {
      this.chat.archiveConversation(c.id, true).subscribe({
        next: () => {
          this.conversations.update((list) => list.filter((x) => x.id !== c.id));
          this.selected.set(null);
          this.currentRoomId = null;
        },
        error: (err) => this.error.set(err?.message || 'Archive failed.')
      });
      return;
    }
    this.chat.leaveGroup(c.id).subscribe({
      next: () => {
        this.conversations.update((list) => list.filter((x) => x.id !== c.id));
        this.selected.set(null);
        this.currentRoomId = null;
      },
      error: (err) => this.error.set(err?.message || 'Leave failed.')
    });
  }

  openNewDm(): void {
    this.showNewDm.set(true);
    this.showNewGroup.set(false);
    this.loadDirectory();
  }

  openNewGroup(): void {
    this.showNewGroup.set(true);
    this.showNewDm.set(false);
    this.groupName.set('');
    this.groupDesc.set('');
    this.groupMembers.set([]);
    this.loadDirectory();
  }

  loadDirectory(search = ''): void {
    this.chat.listUsers(search || undefined).subscribe({
      next: (users) => this.directory.set(users),
      error: () => this.directory.set([])
    });
  }

  onDirSearch(value: string): void {
    this.dirSearch.set(value);
    this.loadDirectory(value);
  }

  startDm(user: ChatUserOption): void {
    this.chat.startDirect(user.userId).subscribe({
      next: (c) => {
        this.showNewDm.set(false);
        this.conversations.update((list) => list.some((x) => x.id === c.id) ? list : [c, ...list]);
        void this.openConversation(c);
      },
      error: (err) => this.error.set(err?.message || 'Could not start chat.')
    });
  }

  toggleGroupMember(userId: string): void {
    this.groupMembers.update((ids) => ids.includes(userId) ? ids.filter((id) => id !== userId) : [...ids, userId]);
  }

  createGroup(): void {
    const name = this.groupName().trim();
    if (!name) return;
    this.chat.createGroup({
      name,
      description: this.groupDesc().trim() || undefined,
      memberUserIds: this.groupMembers()
    }).subscribe({
      next: (c) => {
        this.showNewGroup.set(false);
        this.conversations.update((list) => [c, ...list]);
        void this.openConversation(c);
      },
      error: (err) => this.error.set(err?.message || 'Could not create group.')
    });
  }

  openSearchHit(hit: ChatSearchHit): void {
    const c = this.conversations().find((x) => x.id === hit.roomId);
    if (c) void this.openConversation(c);
    else this.chat.getConversation(hit.roomId).subscribe({
      next: (conv) => void this.openConversation(conv),
      error: () => undefined
    });
  }

  openEmployeeProfile(member: ChatMember): void {
    void this.router.navigate(['/employees'], { queryParams: { q: member.displayName } });
  }

  fileUrl(url?: string | null): string {
    return this.chat.fileUrl(url);
  }

  formatBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  }

  formatTime(iso?: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }

  presenceClass(status?: number | null): string {
    if (status === 1) return 'online';
    if (status === 2) return 'away';
    if (status === 3) return 'busy';
    return 'offline';
  }

  isMine(m: ChatMessage): boolean {
    return m.senderId === this.me();
  }

  dayLabel(iso: string): string {
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  }

  showDaySeparator(index: number): boolean {
    const msgs = this.messages();
    if (index === 0) return true;
    const prev = new Date(msgs[index - 1].createdAt).toDateString();
    const cur = new Date(msgs[index].createdAt).toDateString();
    return prev !== cur;
  }

  taskLink(content: string): string | null {
    const m = content.match(/\/(?:issues|tasks|project-tasks)\/[a-f0-9-]+/i);
    return m ? m[0] : null;
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.threadScroll?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 30);
  }
}
